import "react-tooltip/dist/react-tooltip.css";
import "reactflow/dist/style.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light-border.css";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import classNames from "classnames";
import { cloneDeep, flatten, groupBy, isEqual, pick } from "lodash-es";
import { nanoid } from "nanoid";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Item, ItemParams, Menu, Separator, Submenu, useContextMenu } from "react-contexify";
import { ErrorBoundary } from "react-error-boundary";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  Panel,
  useReactFlow,
} from "reactflow";
import { yDocToProsemirrorJSON } from "y-prosemirror";
import * as Y from "yjs";
import { find } from "yjs/dist/src/internals";

import longevityEdges from "@/components/Page/tt-data/longevity.edges.json";
import longevityPositions from "@/components/Page/tt-data/longevity.positions.json";
import { useActiveEditor, useQueries } from "@/hooks";
import { getPage } from "@/hooks/useQueries/pages";
import { removeNodesById } from "@/utils/block-helpers";

import { addPapersToGraph, findCitations, findReferences } from "../LLMSidebar/semantic";
import styles from "./Graph.module.scss";
import GraphEdge from "./GraphEdge";
import GraphNode from "./GraphNode";
import useEdgesStateSynced from "./useEdgesStateSynced";
import useNodesStateSynced from "./useNodesStateSynced";
import { getEditorNodes, getJsonBlockIds, setNodeLoading } from "./utils";

const nodeTypes = { GraphNode, SyncedNode: GraphNode };
const edgeTypes = { default: GraphEdge };

const MENU_ID = "GraphNode";
const MENU_ID_EDGE = "Edge";

const colors = [
  { value: "None", color: "" },
  { value: "Purple", color: "#5B28CE" },
  { value: "Blue", color: "#336EF3" },
  { value: "Light Blue", color: "#54BFF9" },
  { value: "Green", color: "#76CA48" },
  { value: "Aqua", color: "#8ADFCD" },
  { value: "Red", color: "#EA6441" },
  { value: "Orange", color: "#F2A33A" },
  { value: "Yellow", color: "#F9DE4B" },
  { value: "Pink", color: "#E955D8" },
  { value: "Brown", color: "#B0693A" },
];

const progress = [
  { label: "25%", value: 25 },
  { label: "50%", value: 50 },
  { label: "75%", value: 75 },
  { label: "100%", value: 100 },
  { label: "None", value: 0 },
];

interface GraphProps extends React.SelectHTMLAttributes<HTMLDivElement> {
  pageId: string | undefined;
  editor: Editor;
  ydoc: Y.Doc;
  isClosable?: boolean;
  active: boolean;
}

const intersection = (set1, set2) => new Set([...set1].filter((x) => set2.has(x)));
const difference = (set1, set2) => new Set([...set1].filter((x) => !set2.has(x)));

// Array operations, built using the set ones
// NB: Arrays are NOT Sets, and there is some information lost in the conversion.
// But Sets are the proper data structure for unordered collections of unique values.
const intersectionA = (arr1, arr2) => Array.from(intersection(new Set(arr1), new Set(arr2)));
const differenceA = (arr1, arr2) => Array.from(difference(new Set(arr1), new Set(arr2)));

export default forwardRef<HTMLDivElement, GraphProps>(
  ({ pageId, editor, className, ydoc, active, isClosable = false, ...props }, ref) => {
    const { currentProject } = useQueries();
    const queryClient = useQueryClient();
    const reactFlowInstance = useReactFlow();
    const { setActiveGraph } = useActiveEditor();
    const reactFlowWrapper = useRef<HTMLDivElement>(null);

    const { data: page } = useQuery({
      queryKey: ["pages", currentProject?.id, pageId],
      queryFn: ({ signal }) => getPage(signal, currentProject?.id, pageId),
      enabled: Boolean(currentProject?.id && pageId),
      retry: false,
    });

    const [pageContent, setPageContent] = useState<JSONContent>();
    const [nodeIds, setNodeIds] = useState<string[]>([]);

    useEffect(() => {
      if (active && reactFlowInstance) {
        setActiveGraph(reactFlowInstance);
      }
    }, [active, reactFlowInstance]);

    const edgesMap = useRef<Y.Map<Edge>>();
    const nodesMap = useRef<Y.Map<Node>>();
    const [nodes, onNodesChange] = useNodesStateSynced(nodeIds, nodesMap.current, edgesMap.current);
    const [edges, onEdgesChange, onConnect] = useEdgesStateSynced(edgesMap.current);

    const { show } = useContextMenu({ id: MENU_ID });
    const { show: showEdgeContextMenu } = useContextMenu({ id: MENU_ID_EDGE });

    useEffect(() => {
      if (pageId) {
        nodesMap.current = ydoc.getMap<Node>("nodes");
        edgesMap.current = ydoc.getMap<Edge>("edges");
      }
    }, [pageId, ydoc, nodesMap]);

    useEffect(() => {
      const update = ({ editor }: { editor: Editor }) => {
        setPageContent(editor?.getJSON());
      };
      setPageContent(editor?.getJSON());
      // @ts-ignore
      editor?.on("update", update);
      return () => {
        // @ts-ignore
        editor?.off("update", update);
      };
    }, [editor]);
    const relayout = (selection = []) => {
      ydoc.transact(() => {
        let stable = false;
        const maxIterations = 10; // Set a max to prevent infinite loops
        let iteration = 0;

        while (!stable && iteration < maxIterations) {
          stable = true; // Assume this iteration will get it right
          const newNodes = cloneDeep(nodes);
          const groupedNodes = groupBy(newNodes, "parentNode");

          const dimensions = {};
          const ids = selection.map((n) => n.id);

          for (const [parentId, childNodes] of Object.entries(groupedNodes)) {
            if (selection.length && !ids.includes(parentId)) continue;
            let x = 20;
            let y = 50;
            let largestX = 0;
            let largestY = 0;
            let largestHeight = 0;
            let counter = 1;

            const sortedNodes = childNodes
              .sort((a, b) => (a?.data?.label ?? "").localeCompare(b?.data?.label ?? ""))
              .sort((a, b) => (b.id in groupedNodes) - (a.id in groupedNodes));

            sortedNodes.forEach((node) => {
              const mapNode = cloneDeep(nodesMap.current?.get(node.id));
              if (!mapNode || mapNode.hidden || (selection.length && !ids.includes(node.id)))
                return;

              if (!mapNode.style) mapNode.style = { width: 100, height: 50 };

              const w = parseInt(mapNode.style.width, 10);
              const h = parseInt(mapNode.style.height, 10);

              if (counter % 3 === 1 && counter > 1) {
                x = 20;
                y += largestHeight + 20;
                largestHeight = 0;
              }

              const newX = x;
              const newY = y;

              // Check if the node needs to move
              if (mapNode.position.x !== newX || mapNode.position.y !== newY) {
                stable = false; // Need another iteration
                mapNode.position = { x: newX, y: newY };
              }

              x += w + 20;
              largestX = Math.max(largestX, x);
              largestY = Math.max(largestY, y);
              largestHeight = Math.max(largestHeight, h);

              nodesMap.current?.set(node.id, mapNode);

              counter += 1;
            });

            dimensions[parentId] = {
              width: largestX,
              height: largestY + largestHeight + 20,
            };

            const parentNode = newNodes.find((n) => n.id === parentId);
            if (parentNode) {
              const parentMapNode = nodesMap.current?.get(parentNode.id);
              if (parentMapNode) {
                nodesMap.current?.set(parentNode.id, {
                  ...parentMapNode,
                  style: dimensions[parentId],
                });
              }
            }
          }

          // If the layout didn't change this iteration, we are done
          if (stable) {
            break;
          }

          iteration++;
        }

        // ... Update view or state as needed
      });
    };

    const updateNodes = (newNodes: Node[]) => {
      if (!nodesMap.current) return;
      if (!newNodes.length) return;
      ydoc.transact(() => {
        const crdtNodeIds = Array.from(nodesMap.current.keys());
        const newNodesObject: { [key: string]: Node } = newNodes.reduce(
          (obj: { [key: string]: Node }, item: Node) => {
            obj[item.id] = item;
            return obj;
          },
          {}
        );
        const pageNodeIds = Object.keys(newNodesObject);

        // const idsToDelete = differenceA(crdtNodeIds, pageNodeIds);
        const idsToAdd = differenceA(pageNodeIds, crdtNodeIds);
        const idsToUpdate = intersectionA(crdtNodeIds, pageNodeIds);
        // idsToDelete.forEach((id) => {
        //   // nodesMap.current?.delete(id);
        //   // nodesMap.current?.set(id, { ...newNodesObject[id], deleted: true });
        // });
        idsToAdd.forEach((id) => {
          nodesMap.current?.set(id, newNodesObject[id]);
        });
        idsToUpdate.forEach((id) => {
          const old = nodesMap.current?.get(id);
          if (!old) return;
          const newObject = {
            ...old,
            ...pick(newNodesObject[id], ["extent", "parentNode", "type"]),
            data: { ...old.data, ...newNodesObject[id].data },
          };
          if (!isEqual(old, newObject)) {
            nodesMap.current?.set(id, newObject);
          }
        });
      });
    };

    useEffect(() => {
      if (!pageContent || !pageContent?.content || !nodesMap.current) return;

      const handleDebounce = setTimeout(() => {
        const { ids, nodes } = getEditorNodes(editor);
        setNodeIds(ids);
        updateNodes(nodes);
      }, 250);
      return () => clearTimeout(handleDebounce);
    }, [pageContent]);

    const getNodeAndAllChildren = (selectedNodes: Node[]) => {
      const getAllNodes = (id: string, data: Node[] = []) => {
        const nodesToRelayout: Node[] = nodes.filter((n) => n.parentNode == id);
        nodesToRelayout.forEach((n) => getAllNodes(n.id, data));
        if (nodesToRelayout.length) data.push(...nodesToRelayout);
      };
      const allNodes: Node[] = [];
      selectedNodes.forEach((n) => getAllNodes(n.id, allNodes));
      return flatten([...allNodes, ...selectedNodes]);
    };

    const onNodeCollapse = async ({ props }: { props?: { selectedNodes: Node[] } }) => {
      props?.selectedNodes.forEach((node) => {
        nodesMap.current?.set(node.id, {
          ...node,
          style: {
            width: 200,
            height: 80,
          },
          data: { ...node.data, collapsed: !node?.data.collapsed },
        });
      });
    };

    const onNodeContextMenu = (event: React.MouseEvent, node: Node) => {
      const selectedNodes = nodes?.filter((n) => n.selected);
      show({ event, props: { selectedNodes: selectedNodes.length ? selectedNodes : [node] } });
    };

    const onEdgeContextMenu = (event: React.MouseEvent, edge: Edge) => {
      const seletedEdges = edges?.filter((n) => n.selected);
      showEdgeContextMenu({
        event,
        props: { selectedEdges: seletedEdges.length ? seletedEdges : [edge] },
      });
    };

    const onSetColor = ({
      data,
      props,
    }: {
      data?: { color: string };
      props?: { selectedNodes: Node[] };
    }) => {
      props?.selectedNodes.forEach((node) => {
        const foundNode = nodes.find((n) => n.id == node.id);
        if (foundNode) {
          nodesMap.current?.set(foundNode.id, {
            ...foundNode,
            data: { ...foundNode.data, borderColor: data.color },
          });
        }
      });
    };

    const onSetProgress = ({
      data,
      props,
    }: {
      data?: { value: string };
      props?: { selectedNodes: Node[] };
    }) => {
      props?.selectedNodes.forEach((node) => {
        const foundNode = nodes.find((n) => n.id == node.id);
        if (foundNode) {
          nodesMap.current?.set(foundNode.id, {
            ...foundNode,
            data: { ...foundNode.data, progress: data.value },
          });
        }
      });
    };

    const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string) => {
      if (!event.dataTransfer) return;
      event.dataTransfer.setData("application/lateral/type", nodeType);
      event.dataTransfer.effectAllowed = "move";
    };

    const onDragOver = useCallback<React.DragEventHandler<HTMLDivElement>>(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!event.dataTransfer) return;
        event.dataTransfer.dropEffect = "move";
      },
      []
    );

    const onDrop = useCallback<React.DragEventHandler<HTMLDivElement>>(
      async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!nodesMap.current) {
          console.log("still initialising");
          return;
        }

        const position = reactFlowInstance?.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        const id = nanoid();
        const content = {
          id,
          created: new Date().toJSON(),
          type: "GraphNode",
          position,
          data: { label: "New node" },
          expandParent: false,
          style: {
            width: 200,
            height: 80,
          },
        };

        nodesMap.current.set(id, content);
        editor.commands.insertContentAt(editor.view.state.doc.content.size, [
          { type: "paragraph", content: [] },
          {
            type: "listItem",
            attrs: {
              blockId: id,
            },
            content: [
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: "New node",
                  },
                ],
              },
            ],
          },
        ]);
      },
      [editor, reactFlowInstance, currentProject, nodesMap]
    );

    const onArrowChange = ({ id, props: { selectedEdges } }: ItemParams) => {
      if (!edgesMap.current) return;
      const marker: any = {};
      const markerContent = {
        type: MarkerType.Arrow,
        width: 30,
        height: 30,
      };
      if (id == "marker-end") {
        marker.markerEnd = markerContent;
        marker.markerStart = undefined;
      }
      if (id == "marker-start") {
        marker.markerStart = markerContent;
      }
      if (id == "marker-both") {
        marker.markerEnd = markerContent;
        marker.markerStart = markerContent;
      }
      if (id == "marker-clear") {
        marker.markerEnd = undefined;
        marker.markerStart = undefined;
      }
      selectedEdges.forEach((edge: Edge) => {
        edgesMap?.current?.set(edge.id, {
          ...edge,
          ...marker,
        });
      });
    };

    if (!page?.content) {
      return (
        <div {...props} className={classNames(className, styles.border)}>
          <div className={styles.loader}>Loading...</div>
        </div>
      );
    }

    const onNodesDelete = (nodesToDelete: Node[]) => {
      console.log("Deleting nodes", nodesToDelete);
      if (
        nodesToDelete.length == 1 ||
        (nodesToDelete.length > 1 &&
          window.confirm(
            `Are you sure you want to delete ${nodesToDelete.length} nodes & subnodes?`
          ))
      ) {
        const nodeIds = nodesToDelete.map((n) => n.id);
        const content = editor.getJSON()["content"];
        if (content) {
          const updated = removeNodesById(content, nodeIds);
          editor.commands.setContent(updated, true);
        }
      }
    };

    return (
      <div {...props} className={classNames(className, styles.wrapper, styles.border)}>
        <div className={styles.nodesCreation}>
          <div
            className={styles.node}
            onDragStart={(event) => onDragStart(event, "QuestionNode")}
            draggable
          >
            Add node
          </div>
        </div>
        <ErrorBoundary
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <div className={styles.error}>
              <p>Error displaying Graph, please report to max@lateral.io:</p>
              <pre style={{ color: "red" }}>{error.message}</pre>
            </div>
          )}
        >
          <div className={styles.reactflowWrapper} ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodesDelete={onNodesDelete}
              edgesUpdatable={true}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onConnect={onConnect}
              onEdgeUpdate={(oldEdge, newConnection) => {
                if (!edgesMap?.current) return;
                edgesMap.current.set(oldEdge.id, {
                  ...oldEdge,
                  ...newConnection,
                } as Edge);
              }}
              minZoom={0.1}
              // onNodeDoubleClick={onNodeDoubleClick}
              onNodeClick={(e, n) => console.log(n)}
              onNodeContextMenu={onNodeContextMenu}
              onEdgeContextMenu={onEdgeContextMenu}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
            >
              <Controls />
              <MiniMap pannable={true} />
              <Background gap={12} size={1} />
            </ReactFlow>
          </div>

          <Panel position="bottom-right" className={styles.buttonsBR}>
            <button
              style={{ display: "none" }}
              onClick={async () => {
                if (!nodesMap.current) return;

                Array.from(nodesMap.current.values()).forEach((node) => {
                  nodesMap?.current.set(node.id, {
                    ...node,
                    expandParent: false,
                    position: {
                      x:
                        longevityPositions[node.id].x -
                        (node.parentNode ? longevityPositions[node.parentNode].x : 0),
                      y:
                        longevityPositions[node.id].y -
                        (node.parentNode ? longevityPositions[node.parentNode].y : 0),
                    },
                    zIndex: longevityPositions[node.id].level,
                    style: {
                      width: longevityPositions[node.id].width,
                      height: longevityPositions[node.id].height,
                    },
                  });
                });

                longevityEdges.forEach((edge) => {
                  if (!edgesMap.current) return;
                  const id = `edge-${edge.source}-${edge.target}`;
                  // console.log(`Loaded ${id}`);
                  edgesMap.current.set(id, {
                    id,
                    ...edge,
                    source: edge.target,
                    target: edge.source,
                    targetHandle: "target-left",
                    sourceHandle: "target-right",
                  } as Edge);
                });
              }}
            >
              load tt data
            </button>
            Layout:
            <button
              onClick={() => {
                const selectedNodes = nodes.filter((n) => n.selected);
                const nodesToRelayout = getNodeAndAllChildren(selectedNodes);
                for (let i = 0; i < 100; i++) {
                  relayout(nodesToRelayout);
                }
              }}
            >
              Selected
            </button>
            <button
              onClick={() => {
                if (
                  confirm("Are you sure? This will relayout ALL nodes and can't be undone.") == true
                ) {
                  relayout();
                } else {
                }
              }}
            >
              All
            </button>
          </Panel>
        </ErrorBoundary>

        <Menu id={MENU_ID} animation="" className={styles.contextMenu}>
          <Item
            id={"context-collapse"}
            onClick={onNodeCollapse}
            className={styles.contextMenuItem}
            key={`context-collapse`}
          >
            Collapse / Expand
          </Item>
          <Submenu label="Color" className={styles.contextMenuItem}>
            {colors.map((color, i) => (
              <Item
                id={color.color}
                data={{ ...color }}
                onClick={onSetColor}
                className={styles.contextMenuItem}
                key={`context-color-${i}`}
              >
                <div className={styles.color} style={{ backgroundColor: color.color }}></div>
                {color.value}
              </Item>
            ))}
          </Submenu>
          <Submenu label="Progress" className={styles.contextMenuItem}>
            {progress.map((value, i) => (
              <Item
                id={`${value.value}`}
                data={value}
                onClick={onSetProgress}
                className={styles.contextMenuItem}
                key={`context-progress-${i}`}
              >
                {value.label}
              </Item>
            ))}
          </Submenu>
          <Separator />

          <Item
            id={"context-references"}
            onClick={async ({ props }: { props?: { selectedNodes: Node[] } }) => {
              if (props?.selectedNodes?.length) {
                let i = 0;
                for (const node of props.selectedNodes) {
                  try {
                    setNodeLoading(node.id, nodesMap.current, true);
                    const results = await findReferences(node.data?.paper?.paperId);
                    if (!results?.data?.data?.length) {
                      window.alert("No citations found");
                    } else {
                      addPapersToGraph(
                        {
                          ...node,
                          position: { x: node.position.x + 200 * i, y: node.position.y + 250 * i },
                        },
                        results?.data?.data?.map((p) => p.citedPaper).slice(0, 5),
                        currentProject,
                        nodesMap?.current,
                        edgesMap?.current,
                        editor,
                        queryClient,
                        "References"
                      );
                      i++;
                    }
                  } catch (e) {
                    window.alert(e);
                  }
                  setNodeLoading(node.id, nodesMap.current, false);
                }
              }
            }}
            className={styles.contextMenuItem}
            key={`context-references`}
          >
            Find References
          </Item>

          <Item
            id={"context-citations"}
            onClick={async ({ props }: { props?: { selectedNodes: Node[] } }) => {
              if (props?.selectedNodes?.length) {
                let i = 0;
                for (const node of props.selectedNodes) {
                  try {
                    setNodeLoading(node.id, nodesMap.current, true);
                    const results = await findCitations(node.data?.paper?.paperId);
                    if (!results?.data?.data?.length) {
                      window.alert("No citations found");
                    } else {
                      addPapersToGraph(
                        {
                          ...node,
                          position: { x: node.position.x + 200 * i, y: node.position.y + 250 * i },
                        },
                        results?.data?.data?.map((p) => p.citingPaper).slice(0, 5),
                        currentProject,
                        nodesMap?.current,
                        edgesMap?.current,
                        editor,
                        queryClient,
                        "Citations"
                      );
                      i++;
                    }
                  } catch (e) {
                    window.alert(e);
                  }
                  setNodeLoading(node.id, nodesMap.current, false);
                }
              }
            }}
            className={styles.contextMenuItem}
            key={`context-citations`}
          >
            Find Citations
          </Item>
        </Menu>

        <Menu id={MENU_ID_EDGE} animation="" className={styles.contextMenu}>
          <Item
            id={"marker-start"}
            onClick={onArrowChange}
            className={styles.contextMenuItem}
            key={`marker-start`}
          >
            Arrow start
          </Item>
          <Item
            id={"marker-end"}
            onClick={onArrowChange}
            className={styles.contextMenuItem}
            key={`marker-end`}
          >
            Arrow end
          </Item>
          <Item
            id={"marker-both"}
            onClick={onArrowChange}
            className={styles.contextMenuItem}
            key={`marker-both`}
          >
            Arrow both ends
          </Item>
          <Item
            id={"marker-clear"}
            onClick={onArrowChange}
            className={styles.contextMenuItem}
            key={`marker-clear`}
          >
            Clear arrows
          </Item>
        </Menu>
      </div>
    );
  }
);
